import React, { useState } from 'react'
import { CreditCard, Lock, User, Mail, CreditCard as IdCard } from 'lucide-react'

interface CardFormProps {
  onPayment: (cardData: any) => void
  loading?: boolean
  error?: string | null
}

interface CardData {
  cardNumber: string
  expiryMonth: string
  expiryYear: string
  securityCode: string
  cardholderName: string
  identificationType: string
  identificationNumber: string
  email: string
}

const CardForm: React.FC<CardFormProps> = ({ onPayment, loading = false, error = null }) => {
  const [formData, setFormData] = useState<CardData>({
    cardNumber: '',
    expiryMonth: '',
    expiryYear: '',
    securityCode: '',
    cardholderName: '',
    identificationType: 'CPF',
    identificationNumber: '',
    email: ''
  })

  const [formErrors, setFormErrors] = useState<Partial<CardData>>({})

  const formatCardNumber = (value: string) => {
    return value.replace(/\D/g, '').replace(/(\d{4})(?=\d)/g, '$1 ')
  }


  const formatSecurityCode = (value: string) => {
    return value.replace(/\D/g, '').slice(0, 4)
  }

  const formatIdentificationNumber = (value: string) => {
    return value.replace(/\D/g, '')
  }

  const validateForm = (): boolean => {
    const errors: Partial<CardData> = {}

    if (!formData.cardNumber || formData.cardNumber.replace(/\s/g, '').length < 13) {
      errors.cardNumber = 'Número do cartão inválido'
    }

    if (!formData.expiryMonth || !formData.expiryYear) {
      errors.expiryMonth = 'Data de validade inválida'
    }

    if (!formData.securityCode || formData.securityCode.length < 3) {
      errors.securityCode = 'CVV inválido'
    }

    if (!formData.cardholderName.trim()) {
      errors.cardholderName = 'Nome do portador é obrigatório'
    }

    if (!formData.identificationNumber || formData.identificationNumber.length < 11) {
      errors.identificationNumber = 'CPF/CNPJ inválido'
    }

    if (!formData.email || !/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = 'Email inválido'
    }

    setFormErrors(errors)
    return Object.keys(errors).length === 0
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    
    if (validateForm()) {
      onPayment(formData)
    }
  }

  const handleInputChange = (field: keyof CardData, value: string) => {
    let formattedValue = value

    switch (field) {
      case 'cardNumber':
        formattedValue = formatCardNumber(value)
        break
      case 'expiryMonth':
      case 'expiryYear':
        if (field === 'expiryMonth' && value.length === 2) {
          // Auto-focus no ano quando mês estiver completo
          const yearInput = document.getElementById('expiryYear') as HTMLInputElement
          if (yearInput) yearInput.focus()
        }
        break
      case 'securityCode':
        formattedValue = formatSecurityCode(value)
        break
      case 'identificationNumber':
        formattedValue = formatIdentificationNumber(value)
        break
    }

    setFormData(prev => ({
      ...prev,
      [field]: formattedValue
    }))

    // Limpar erro do campo quando usuário começar a digitar
    if (formErrors[field]) {
      setFormErrors(prev => ({
        ...prev,
        [field]: undefined
      }))
    }
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border p-6">
      <div className="flex items-center mb-6">
        <CreditCard className="w-6 h-6 text-blue-600 mr-3" />
        <h3 className="text-lg font-semibold text-gray-900">Dados do Cartão</h3>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-red-700 text-sm">{error}</p>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Número do Cartão */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Número do Cartão *
          </label>
          <div className="relative">
            <input
              type="text"
              value={formData.cardNumber}
              onChange={(e) => handleInputChange('cardNumber', e.target.value)}
              placeholder="0000 0000 0000 0000"
              maxLength={19}
              className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                formErrors.cardNumber ? 'border-red-300' : 'border-gray-300'
              }`}
            />
            <Lock className="absolute right-3 top-2.5 w-4 h-4 text-gray-400" />
          </div>
          {formErrors.cardNumber && (
            <p className="mt-1 text-sm text-red-600">{formErrors.cardNumber}</p>
          )}
        </div>

        {/* Data de Validade e CVV */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Validade *
            </label>
            <div className="flex space-x-2">
              <input
                type="text"
                value={formData.expiryMonth}
                onChange={(e) => handleInputChange('expiryMonth', e.target.value)}
                placeholder="MM"
                maxLength={2}
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  formErrors.expiryMonth ? 'border-red-300' : 'border-gray-300'
                }`}
              />
              <span className="flex items-center text-gray-500">/</span>
              <input
                type="text"
                id="expiryYear"
                value={formData.expiryYear}
                onChange={(e) => handleInputChange('expiryYear', e.target.value)}
                placeholder="AA"
                maxLength={2}
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  formErrors.expiryMonth ? 'border-red-300' : 'border-gray-300'
                }`}
              />
            </div>
            {formErrors.expiryMonth && (
              <p className="mt-1 text-sm text-red-600">{formErrors.expiryMonth}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              CVV *
            </label>
            <input
              type="text"
              value={formData.securityCode}
              onChange={(e) => handleInputChange('securityCode', e.target.value)}
              placeholder="123"
              maxLength={4}
              className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                formErrors.securityCode ? 'border-red-300' : 'border-gray-300'
              }`}
            />
            {formErrors.securityCode && (
              <p className="mt-1 text-sm text-red-600">{formErrors.securityCode}</p>
            )}
          </div>
        </div>

        {/* Nome do Portador */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Nome do Portador *
          </label>
          <div className="relative">
            <input
              type="text"
              value={formData.cardholderName}
              onChange={(e) => handleInputChange('cardholderName', e.target.value)}
              placeholder="Nome como está no cartão"
              className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                formErrors.cardholderName ? 'border-red-300' : 'border-gray-300'
              }`}
            />
            <User className="absolute right-3 top-2.5 w-4 h-4 text-gray-400" />
          </div>
          {formErrors.cardholderName && (
            <p className="mt-1 text-sm text-red-600">{formErrors.cardholderName}</p>
          )}
        </div>

        {/* Email */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Email *
          </label>
          <div className="relative">
            <input
              type="email"
              value={formData.email}
              onChange={(e) => handleInputChange('email', e.target.value)}
              placeholder="seu@email.com"
              className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                formErrors.email ? 'border-red-300' : 'border-gray-300'
              }`}
            />
            <Mail className="absolute right-3 top-2.5 w-4 h-4 text-gray-400" />
          </div>
          {formErrors.email && (
            <p className="mt-1 text-sm text-red-600">{formErrors.email}</p>
          )}
        </div>

        {/* Documento */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Documento *
          </label>
          <div className="flex space-x-2">
            <select
              value={formData.identificationType}
              onChange={(e) => setFormData(prev => ({ ...prev, identificationType: e.target.value }))}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="CPF">CPF</option>
              <option value="CNPJ">CNPJ</option>
            </select>
            <div className="relative flex-1">
              <input
                type="text"
                value={formData.identificationNumber}
                onChange={(e) => handleInputChange('identificationNumber', e.target.value)}
                placeholder={formData.identificationType === 'CPF' ? '000.000.000-00' : '00.000.000/0000-00'}
                maxLength={formData.identificationType === 'CPF' ? 11 : 14}
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  formErrors.identificationNumber ? 'border-red-300' : 'border-gray-300'
                }`}
              />
              <IdCard className="absolute right-3 top-2.5 w-4 h-4 text-gray-400" />
            </div>
          </div>
          {formErrors.identificationNumber && (
            <p className="mt-1 text-sm text-red-600">{formErrors.identificationNumber}</p>
          )}
        </div>

        {/* Botão de Pagamento */}
        <div className="pt-4">
          <button
            type="submit"
            disabled={loading}
            className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors flex items-center justify-center"
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Processando Pagamento...
              </>
            ) : (
              <>
                <CreditCard className="w-4 h-4 mr-2" />
                Pagar com Cartão
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  )
}

export default CardForm
